import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { EditActionItemContextDto, FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  actionItemGuid: string;
  contextActionItemGuid: string | null;
  currentContextActionItemGuid: string | null;
  planGuid: string;
};

const useEditActionItemContextRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemContextRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditActionItemContextDto = {
      contextActionItemGuid: variables.contextActionItemGuid,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/context`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemContextRequest };
};

const useEditActionItemContext = () => {
  const { editActionItemContextRequest } = useEditActionItemContextRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemContextRequest,
    onSuccess: (_, variables) => {
      queryClient.refetchQueries(queryKeys.actionItems.getActionItemSummary(variables.actionItemGuid));
      queryClient.refetchQueries(queryKeys.actionItems.getActionItemDetail(variables.actionItemGuid));
      queryClient.refetchQueries(queryKeys.actionItems.getActionItemFeed(variables.actionItemGuid));
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);

      queryClient.refetchQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.refetchQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
      signalR.sendRefetchPlan(variables.planGuid);

      if (variables.currentContextActionItemGuid) {
        queryClient.refetchQueries(queryKeys.actionItems.getActionItemDetail(variables.currentContextActionItemGuid));
        queryClient.refetchQueries(queryKeys.actionItems.getActionItemSummary(variables.currentContextActionItemGuid));
        signalR.sendRefetchActionItem(variables.currentContextActionItemGuid);
      }

      if (variables.contextActionItemGuid) {
        queryClient.refetchQueries(queryKeys.actionItems.getActionItemDetail(variables.contextActionItemGuid));
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
      }
    },
  });
};

export { useEditActionItemContext };
